import { BCMSDate,BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import { PageContainer, PageContentView } from 'components/shared';
import Classes, { Account, OneTimeWireTransfer, ScheduledWire, WireActivity, WireHistory } from "model";
import React from 'react';
import PendingWireWidget from './components/PendingWireWidget';
import ProcessedWireTemplatesWidget from './components/ProcessedWireTemplatesWidget';
import ScheduledWiresWidget from './components/ScheduledWiresWidget';
import WireHistoryWidget from './components/WireHistoryWidget';
import WireSearch from './components/WireHistoryWidget/classes/WireSearch';
import ManageWireActivityWidget from "./components/ManageWireActivityWidget";
import { InfoBanner } from "components/shared/views/ConfirmBannerView";

import "./styles.css";
import "./mobile.css";


/**
 * @function WiresActivityPage
 * @return {React.Component}
 * @exported true
 * @description The page for wire activity
 */

export default function WiresActivityPage(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  // Branding options needed for captures
  const branding = useModel().branding.options;
  window.BRANDING = { 
      "wires_abi22" : branding.wires_abi22
  };

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    // Modify the data in this page from a widget.
    modifyPageDataFromWidget: (widgetID, dataProp) => val => {
      BCMSStateManagerCRUD.init(state)
      .update([ "data", widgetID, dataProp ], val)
      .submit(setState);
    },
    closeWireApproveBanner: () => {
      BCMSStateManagerCRUD.init(state)
      .update([ "ui", "showWireApproveBanner" ], false)
      .submit(setState);
    },

    //START: Pending Wires Widget Actions
    pendingWireCheckAll: () => {
      const pendingTransfers = state.data.pendingWireWidgetData.oneTimeWireTransfers;
      // update all rows to have isChecked
      const updatedPendingTransfers = pendingTransfers.map(item => {
        item.isChecked = !state.ui.pendingWireWidgetUI.isCheckedAll;
        return item;
      });

      // Check if all rows are checked, then set isCheckedAll to true, else false
      const hasCheckedRows = updatedPendingTransfers.filter(item => item.isChecked).length > 0;

      BCMSStateManagerCRUD.init(state)
        .update(["ui", "pendingWireWidgetUI", "isCheckedAll"], !state.ui.pendingWireWidgetUI.isCheckedAll)
        .update(["ui", "pendingWireWidgetUI", "isRejectedDisabled"], !hasCheckedRows)
        .update(["ui", "pendingWireWidgetUI", "isApprovedDisabled"], !hasCheckedRows)
        .update(["data", "pendingWireWidgetData", "oneTimeWireTransfers"], updatedPendingTransfers)
        .submit(setState);
    },
    pendingWireCheckedRow: (id) => (value) => {
      const pendingTransfers = state.data.pendingWireWidgetData.oneTimeWireTransfers;

      //update row with id to have isChecked = value
      const updatedPendingTransfers = pendingTransfers.map(item => {
        if (item.id === id) {
          item.isChecked = value;
        }
        return item;
      });

      //Check if all rows are checked, then set isCheckedAll to true, else false
      const hasCheckedRows = updatedPendingTransfers.filter(item => item.isChecked).length > 0;
      const isCheckedAll = updatedPendingTransfers.filter(item => item.isChecked).length === updatedPendingTransfers.length;

      BCMSStateManagerCRUD.init(state)
        .update(["ui", "pendingWireWidgetUI", "isCheckedAll"], isCheckedAll)
        .update(["ui", "pendingWireWidgetUI", "isRejectedDisabled"], !hasCheckedRows)
        .update(["ui", "pendingWireWidgetUI", "isApprovedDisabled"], !hasCheckedRows)
        .update(["data", "pendingWireWidgetData", "oneTimeWireTransfers"], updatedPendingTransfers)
        .submit(setState);

    },
    pendingWireApprove: () => {
      const pendingTransfers = state.data.pendingWireWidgetData.oneTimeWireTransfers;
      const checkedPendingTransfers = pendingTransfers.filter(item => item.isChecked);

      // store delete operations for checked pending wrires
      const updatedPendingTransfers = checkedPendingTransfers.map(item => {
        if (item.id !== undefined && item.id !== null) {
          const id = item.id.toString();
          return OneTimeWireTransfer.delete(id);
        }
      }, []);

      // Store create operations for checked pending wires to create new wire activity records
      const newWireActivities = checkedPendingTransfers.map((item, index) => {
        const wireTime = item.date ? BCMSDate.init(item.date).date.getTime() : 0;
        const newWireActivity = WireActivity.init({
          ...item.record,
          id: parseInt(WireActivity.nextID()) + parseInt(index),
          payeeName: item.name,
          status: "1",
          statusDescription: "pending approval",
          fundingAccount: item.wireFundingAccount.id,
          wireTime
        });

        return newWireActivity;
      }, []);
      const updatedWireActivity = newWireActivities.map(wireActivity => WireActivity.create(wireActivity));
   
      // Update the model with the delete and create operations, then update state.
      BCMSRecord.batch([...updatedPendingTransfers, ...updatedWireActivity], globalController, Classes);
      BCMSStateManagerCRUD.init(state)
        .update(["ui", "pendingWireWidgetUI", "isCheckedAll"], false)
        .update(["ui", "pendingWireWidgetUI", "isRejectedDisabled"], true)
        .update(["ui", "pendingWireWidgetUI", "isApprovedDisabled"], true)
        .update(["data", "pendingWireWidgetData", "oneTimeWireTransfers"], OneTimeWireTransfer.initAll().filter(item => item.status === "pending approval"))
        .update(["data", "wireActivityWidgetData", "wireActivity"], sortByWireTime([
          ...newWireActivities,
          ...state.data.wireActivityWidgetData.wireActivity
        ]))
        .update(["ui", "showWireApproveBanner"], true)
        .submit(setState);
    }
    //END: Pending Wires Widget Actions
  });

  return(
    <PageContainer className="wires-activity-page" state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = () => ({
  pendingWireWidgetData: {
    // no data used for now.
    oneTimeWireTransfers: OneTimeWireTransfer.initAll().filter(item => item.status === "pending approval")
  },
  scheduledWiresWidgetData: {
    // no data used for now.
    scheduledWires: ScheduledWire.initAll()
  },
  wireHistoryWidgetData: {
    accounts: Account.initAllAsset(),
    showSearchOptions: { isActive: false },
    wireHistory: WireHistory.initAll(),
    search: WireSearch.init()
    //showSearchOptions: true,
    // Example of setting search values.
    //search: WireSearch.init({dateRange: "today"})
  },
  wireActivityWidgetData: {
    wireActivity: sortByWireTime(WireActivity.initAll()),
    showSearchOptions: { isActive: false },
  }
});

function sortByWireTime(wires) {
  return [...(wires ?? [])].sort((a, b) => {
    const aWireTime = Number.isFinite(Number(a.wireTime)) ? Number(a.wireTime) : 0;
    const bWireTime = Number.isFinite(Number(b.wireTime)) ? Number(b.wireTime) : 0;

    return bWireTime - aWireTime;
  });
}

// no ui changes for now
export const getDefaultUI = () => ({
  showWireApproveBanner: false,
  pendingWireWidgetUI: {
    isCheckedAll: false,
    isRejectedDisabled: true,
    isApprovedDisabled: true
  },
  scheduledWiresWidgetUI: {
    
  },
  wireHistoryWidgetUI: {

  },
  wireActivityWidgetUI: {
  
  }
});


function PageContent(props) {
  const { data, ui, modifyPageDataFromWidget, pendingWireCheckAll, pendingWireCheckedRow, pendingWireApprove, closeWireApproveBanner} = useStateManager();
  const { branding, settings} = useModel() ?? {};
  const { options } = branding ?? {};
  const hasNotifyPayeeColumn = options.wire_activity_processed_widget_abi22 === "true";
  const action = { modifyPageDataFromWidget: modifyPageDataFromWidget, pendingWireCheckAll, pendingWireCheckedRow, pendingWireApprove};
  const fiName = settings.fiName ?? "";

  return (
  <PageContentView>
    <div className="flex-container flex-vertical flex-width">
      {ui.showWireApproveBanner && <InfoBanner text={`The selected wire transfer(s) were approved and pending for ${fiName} approval.`} onClick={closeWireApproveBanner}/> }
      <PendingWireWidget data={ data.pendingWireWidgetData } ui={ ui.pendingWireWidgetUI } action={ action } />
      <ScheduledWiresWidget data={ data.scheduledWiresWidgetData } ui={ ui.scheduledWiresWidgetUI } action={ action } />
      {/* <WireHistoryWidget data={ data.wireHistoryWidgetData } ui={ ui.wireHistoryWidgetUI } action={ action } /> */}
      <ManageWireActivityWidget data={ data.wireActivityWidgetData } ui={ ui.wireActivityWidgetUI } action={ action } />
      { hasNotifyPayeeColumn && <ProcessedWireTemplatesWidget /> }
    </div>
  </PageContentView>
  );
}